import type { Dtos, UserId } from "@inrev/common";
import { useAdminFetchAgencyPreviews } from "@portal/api/agency/admin";
import { cn } from "@portal/components/lib/utils";
import { Icon } from "@portal/components/primitives";
import { Separator } from "@portal/components/ui/Separator";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import { AdminFormFollowers } from "../../../shared/submission/AdminFormFollowers";

export const QuickSubmitHeader = ({
	title,
	agentPreviews,
	onFollow,
	onUnfollow,
}: {
	title: string;
	agentPreviews?: Dtos.Agent.Preview.Get.Response[];
	onFollow: (id: UserId) => void;
	onUnfollow: (id: UserId) => void;
}) => {
	const { watch, formState } = useFormContext<{
		agencyId: string;
		agentId: string;
		followers: UserId[];
	}>();

	const followers = watch("followers");
	const selectedAgencyId = watch("agencyId");
	const selectedAgentId = watch("agentId");
	const { agencyPreviews } = useAdminFetchAgencyPreviews();
	const followersError = formState.errors?.followers;

	const selectedAgencyName =
		selectedAgencyId && agencyPreviews?.find((a) => a.id === selectedAgencyId)?.displayName;
	const selectedAgentName = useMemo(() => {
		if (!selectedAgentId || !agentPreviews) return undefined;
		const agent = agentPreviews.find((a) => a.id === selectedAgentId);
		return agent ? agent.displayName : undefined;
	}, [selectedAgentId, agentPreviews]);

	return (
		<div className="flex flex-row gap-8 items-center">
			<div className="font-[500] flex items-center gap-2">
				<div>
					{title} {selectedAgencyName ? `for ${selectedAgencyName}` : null}
				</div>
				{selectedAgentName ? (
					<div className="flex flex-row items-center gap-2">
						<Separator orientation="vertical" />
						{selectedAgentName}
					</div>
				) : null}
			</div>
			{agentPreviews ? (
				<AdminFormFollowers
					followers={followers}
					agentPreviews={agentPreviews}
					onFollow={onFollow}
					onUnfollow={onUnfollow}
					error={followersError}
				/>
			) : (
				<div className="relative">
					<Tooltip
						id="followers-tooltip"
						content="Select an agency before setting followers"
						place="top"
					/>
					<div
						data-tooltip-id="followers-tooltip"
						className={cn(
							"flex items-center justify-center w-fit h-[24px] min-h-[24px] space-x-[5.5px] px-[10px] text-[12.5px] font-[550] text-secondary border border-bc-strong bg-surface-base hover:text-primary hover:border-bc-stronger rounded-full opacity-50",
							followersError ? "border-danger" : "",
						)}
					>
						<span>{followers?.length || 0}</span>
						<Icon type="users" size="micro" color="secondary" />
					</div>
					{!selectedAgencyId && (
						<Icon
							type="information-circle"
							size="micro"
							color="tertiary"
							className="absolute -top-2 -right-2 bg-surface-base cursor-pointer"
							data-tooltip-id="followers-tooltip"
						/>
					)}
					{followersError && followersError.message && (
						<span className="absolute text-danger text-[11px] font-normal -top-4 left-0 text-nowrap bg-surface-base bg-opacity-50">
							{followersError.message}
						</span>
					)}
				</div>
			)}
		</div>
	);
};
